import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

// Replace the API_URL constant
const API_URL = `${config.apiUrl}/cyclecount`;

function CycleCountApp() {
  const theme = useTheme();
  const { token, user } = useAuth();
  const [cycleCounts, setCycleCounts] = useState([]);
  const [archivedCounts, setArchivedCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    partNumber: '',
    location: '',
    reportedQty: '',
    notes: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedCount, setSelectedCount] = useState(null);
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const [actualQty, setActualQty] = useState('');
  const [users, setUsers] = useState({});
  const [countComments, setCountComments] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsCount, setDetailsCount] = useState(null);

  useEffect(() => {
    fetchCycleCounts();
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      
      // Create a map of user IDs to names
      const userMap = {};
      data.forEach(user => {
        userMap[user._id] = `${user.firstName} ${user.lastName}`;
      });
      
      setUsers(userMap);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCycleCounts = async () => {
    try {
      setLoading(true);
      
      // Fetch active cycle counts
      const activeResponse = await fetch(`${config.apiUrl}/cyclecount`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!activeResponse.ok) {
        throw new Error('Failed to fetch cycle counts');
      }
      
      const activeData = await activeResponse.json();
      setCycleCounts(activeData);
      
      // Fetch archived cycle counts
      const archivedResponse = await fetch(`${config.apiUrl}/cyclecount/archived`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!archivedResponse.ok) {
        throw new Error('Failed to fetch archived cycle counts');
      }
      
      const archivedData = await archivedResponse.json();
      setArchivedCounts(archivedData);
      
    } catch (error) {
      setError(error.message);
      console.error('Error fetching cycle counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCycleCount = async () => {
    try {
      setError('');
      
      const response = await fetch(`${config.apiUrl}/cyclecount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Non-JSON error response:', errorData);
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      setCycleCounts([data, ...cycleCounts]);
      setDialogOpen(false);
      setFormData({
        partNumber: '',
        location: '',
        reportedQty: '',
        notes: ''
      });
      setSuccess('Cycle count created successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Create cycle count error:', error);
      setError(error.message || 'Failed to create cycle count');
    }
  };

  const handleUpdateCycleCount = async () => {
    try {
      console.log('Updating cycle count:', selectedCount._id, formData);
      
      const response = await fetch(`${API_URL}/${selectedCount._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      // Check if the response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned non-JSON response');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update cycle count');
      }
      
      const updatedCount = await response.json();
      
      // Update the local state
      setCycleCounts(cycleCounts.map(count => 
        count._id === updatedCount._id ? updatedCount : count
      ));
      
      setDialogOpen(false);
      setSuccess('Cycle count updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Update error:', error);
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCompleteCycleCount = async () => {
    try {
      console.log('Completing cycle count with ID:', selectedCount._id);
      console.log('Actual quantity:', actualQty);
      console.log('Comments:', countComments);
      
      // Use a direct URL with no API_URL variable
      const response = await fetch(`${config.apiUrl}/cyclecount/${selectedCount._id}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          actualQty: parseInt(actualQty),
          comments: countComments
        })
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const text = await response.text();
        console.error('Error response text:', text);
        throw new Error(`Failed to complete cycle count: ${response.status}`);
      }
      
      const completedCount = await response.json();
      console.log('Completed count:', completedCount);
      
      // Update the local state
      setCycleCounts(cycleCounts.map(count => 
        count._id === completedCount._id ? completedCount : count
      ));
      
      setCountDialogOpen(false);
      setActualQty('');
      setSuccess('Cycle count completed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Complete error:', error);
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleArchiveCycleCount = async (id) => {
    try {
      setError('');
      
      const response = await fetch(`${config.apiUrl}/cyclecount/${id}/archive`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to archive cycle count');
      }
      
      const data = await response.json();
      
      // Remove from active list
      setCycleCounts(cycleCounts.filter(count => count._id !== id));
      
      // Add to archived list
      setArchivedCounts([data, ...archivedCounts]);
      
      setSuccess('Cycle count archived successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      setError(error.message);
    }
  };

  const handleRestoreCycleCount = async (id) => {
    try {
      setError('');
      
      const response = await fetch(`${config.apiUrl}/cyclecount/${id}/restore`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to restore cycle count');
      }
      
      const data = await response.json();
      
      // Remove from archived list
      setArchivedCounts(archivedCounts.filter(count => count._id !== id));
      
      // Add to active list
      setCycleCounts([data, ...cycleCounts]);
      
      setSuccess('Cycle count restored successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      setError(error.message);
    }
  };

  const handleEditClick = (count) => {
    setSelectedCount(count);
    setFormData({
      partNumber: count.partNumber,
      location: count.location,
      reportedQty: count.reportedQty,
      notes: count.notes || ''
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleCountClick = (count) => {
    setSelectedCount(count);
    setActualQty('');
    setCountComments('');
    setCountDialogOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'open':
        return <Chip label="Open" color="primary" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'archived':
        return <Chip label="Archived" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    
    // Convert to string if it's an ObjectId
    const id = typeof userId === 'object' ? userId.toString() : userId;
    
    // Check if we have a user with this ID
    const user = users[id];
    if (user) {
      return user.firstName && user.lastName 
        ? `${user.firstName} ${user.lastName}`
        : user.username || 'Unknown';
    }
    
    return 'Unknown User';
  };

  const handleUndoComplete = async (countId) => {
    try {
      setError('');
      
      const response = await fetch(`${API_URL}/${countId}/undo`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to undo cycle count');
      }
      
      const updatedCount = await response.json();
      
      // Update the local state
      setCycleCounts(cycleCounts.map(count => 
        count._id === updatedCount._id ? updatedCount : count
      ));
      
      setSuccess('Cycle count reverted to open status');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Undo error:', error);
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleViewDetails = (count) => {
    setDetailsCount(count);
    setDetailsDialogOpen(true);
  };

  const renderActions = (count) => {
    if (count.status === 'archived') {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center'
        }}>
          <Tooltip title="Restore">
            <IconButton 
              onClick={() => handleRestoreCycleCount(count._id)}
              color="primary"
              size="small"
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 0.5,
        alignItems: 'center' 
      }}>
        {count.status === 'open' && (
          <>
            <Tooltip title="Edit">
              <IconButton 
                onClick={() => handleEditClick(count)}
                color="primary"
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Complete Count">
              <IconButton 
                onClick={() => handleCountClick(count)}
                color="success"
                size="small"
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        
        {count.status === 'completed' && (
          <>
            <Tooltip title="View Details">
              <IconButton 
                onClick={() => handleViewDetails(count)}
                color="info"
                size="small"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Undo Complete">
              <IconButton 
                onClick={() => handleUndoComplete(count._id)}
                color="secondary"
                size="small"
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        
        <Tooltip title="Archive">
          <IconButton 
            onClick={() => handleArchiveCycleCount(count._id)}
            color="warning"
            size="small"
          >
            <ArchiveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderCycleCountTable = (counts) => {
    return (
      <TableContainer component={Paper} sx={{ mt: 2, mb: 4, boxShadow: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: theme.palette.primary.main }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Part Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Location</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reported Qty</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Actual Qty</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Reported By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Counted By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Created At</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {counts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No cycle counts found
                </TableCell>
              </TableRow>
            ) : (
              counts.map((count) => (
                <TableRow 
                  key={count._id}
                  sx={{ 
                    '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
                  }}
                >
                  <TableCell>{count.partNumber}</TableCell>
                  <TableCell>{count.location}</TableCell>
                  <TableCell>{count.reportedQty}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {count.status === 'completed' ? (
                      <Box>
                        <Typography variant="body2">
                          {count.actualQty}
                          {count.actualQty !== undefined && count.reportedQty !== undefined && (
                            <Typography 
                              component="span" 
                              variant="caption" 
                              sx={{ 
                                ml: 1,
                                fontWeight: 'bold',
                                color: count.actualQty > count.reportedQty 
                                  ? 'success.main' 
                                  : count.actualQty < count.reportedQty 
                                    ? 'error.main' 
                                    : 'text.secondary'
                              }}
                            >
                              ({count.actualQty - count.reportedQty > 0 ? '+' : ''}
                              {count.actualQty - count.reportedQty})
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{getStatusChip(count.status)}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {count.reportedByUser ? 
                      `${count.reportedByUser.firstName} ${count.reportedByUser.lastName}` : 
                      'Unknown'}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {count.countedByUser ? 
                      `${count.countedByUser.firstName} ${count.countedByUser.lastName}` : 
                      '-'}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {formatDate(count.createdAt)}
                  </TableCell>
                  <TableCell>
                    {renderActions(count)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderHeader = () => {
    // Calculate counts
    const openCount = cycleCounts.filter(count => count.status === 'open').length;
    const completedCount = cycleCounts.filter(count => count.status === 'completed').length;
    const archivedCount = archivedCounts.length;
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }}>
        <Box>
          <Typography variant="h4" component="h1">
            Cycle Count
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1, 
            mt: 1 
          }}>
            <Chip 
              icon={<InventoryIcon />} 
              label={`${openCount} Open`} 
              color="primary" 
              variant="outlined"
              size="small"
            />
            <Chip 
              icon={<CheckCircleIcon />} 
              label={`${completedCount} Completed`} 
              color="success" 
              variant="outlined"
              size="small"
            />
            <Chip 
              icon={<ArchiveIcon />} 
              label={`${archivedCount} Archived`} 
              color="default" 
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
          onClick={() => {
            setEditMode(false);
            setFormData({
              partNumber: '',
              location: '',
              reportedQty: '',
              notes: ''
            });
            setDialogOpen(true);
          }}
        >
          New Count
        </Button>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      {renderHeader()}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Active Counts" />
              <Tab label="Archived Counts" />
            </Tabs>
          </Box>

          {tabValue === 0 && renderCycleCountTable(cycleCounts)}
          {tabValue === 1 && renderCycleCountTable(archivedCounts)}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Cycle Count' : 'Create Cycle Count'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Part Number"
            fullWidth
            value={formData.partNumber}
            onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Location"
            fullWidth
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Reported Quantity"
            fullWidth
            type="number"
            value={formData.reportedQty}
            onChange={(e) => setFormData({ ...formData, reportedQty: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={4}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editMode ? handleUpdateCycleCount : handleCreateCycleCount} 
            variant="contained"
            disabled={!formData.partNumber || !formData.location || !formData.reportedQty}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Count Dialog */}
      <Dialog open={countDialogOpen} onClose={() => setCountDialogOpen(false)}>
        <DialogTitle>Complete Cycle Count</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Part Number: {selectedCount?.partNumber}</Typography>
            <Typography variant="subtitle1">Location: {selectedCount?.location}</Typography>
            <Typography variant="subtitle1">Reported Quantity: {selectedCount?.reportedQty}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TextField
            autoFocus
            margin="dense"
            label="Actual Quantity"
            fullWidth
            type="number"
            value={actualQty}
            onChange={(e) => setActualQty(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Comments"
            fullWidth
            multiline
            rows={3}
            value={countComments}
            onChange={(e) => setCountComments(e.target.value)}
            placeholder="Add any comments about this count..."
            sx={{ mb: 1 }}
          />
          {selectedCount && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Quantity Comparison</Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                p: 2, 
                bgcolor: 'background.paper', 
                borderRadius: 1,
                boxShadow: 1
              }}>
                <Typography>Expected: {selectedCount.reportedQty}</Typography>
                <Typography>
                  Actual: {actualQty || '?'}
                  {actualQty && selectedCount.reportedQty !== undefined && (
                    <Typography 
                      component="span" 
                      sx={{ 
                        ml: 1,
                        fontWeight: 'bold',
                        color: actualQty > selectedCount.reportedQty 
                          ? 'success.main' 
                          : actualQty < selectedCount.reportedQty 
                            ? 'error.main' 
                            : 'text.secondary'
                      }}
                    >
                      ({actualQty - selectedCount.reportedQty > 0 ? '+' : ''}
                      {actualQty - selectedCount.reportedQty})
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCountDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCompleteCycleCount} 
            variant="contained"
            color="success"
            disabled={actualQty === ''}
          >
            Complete Count
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cycle Count Details</DialogTitle>
        <DialogContent>
          {detailsCount && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Part Number:</strong> {detailsCount.partNumber}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Location:</strong> {detailsCount.location}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Reported Quantity:</strong> {detailsCount.reportedQty}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Actual Quantity:</strong> {detailsCount.actualQty}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong> {getStatusChip(detailsCount.status)}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Reported By:</strong> {detailsCount.reportedByUser ? 
                  `${detailsCount.reportedByUser.firstName} ${detailsCount.reportedByUser.lastName}` : 
                  'Unknown'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Counted By:</strong> {detailsCount.countedByUser ? 
                  `${detailsCount.countedByUser.firstName} ${detailsCount.countedByUser.lastName}` : 
                  '-'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Created:</strong> {formatDate(detailsCount.createdAt)}
              </Typography>
              {detailsCount.countedAt && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Counted:</strong> {formatDate(detailsCount.countedAt)}
                </Typography>
              )}
              <Typography variant="subtitle1" gutterBottom>
                <strong>Notes:</strong> {detailsCount.notes || '-'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Comments:</strong> {detailsCount.comments || '-'}
              </Typography>
              {selectedCount && selectedCount.status === 'completed' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Quantity Comparison</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      Expected: {selectedCount.reportedQty}
                    </Typography>
                    <Typography variant="body2">
                      Actual: {selectedCount.actualQty}
                      {selectedCount.actualQty !== undefined && selectedCount.reportedQty !== undefined && (
                        <Typography 
                          component="span" 
                          variant="body2" 
                          sx={{ 
                            ml: 1,
                            fontWeight: 'bold',
                            color: selectedCount.actualQty > selectedCount.reportedQty 
                              ? 'success.main' 
                              : selectedCount.actualQty < selectedCount.reportedQty 
                                ? 'error.main' 
                                : 'text.secondary'
                          }}
                        >
                          ({selectedCount.actualQty - selectedCount.reportedQty > 0 ? '+' : ''}
                          {selectedCount.actualQty - selectedCount.reportedQty})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default CycleCountApp;